import React, { useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// TS type
import { WindowCustomData } from 'page/detail/index';
import { RootState } from '@redux/store';
import { ITHSRInfoData, scrollToRef, ICommonDetailInfo } from '@redux/common';

// import module
import IcRcln from '@magaele/ic_rcln';

// component
import CalendarBlock from './CalendarBlock';
import SessionBlock from './SessionBlock';
import TicketBlock from './TicketBlock';

// shared components
import Reload from '@svg/Reload'; // svg component
import ResetIcon from '@svg/ResetIcon'; // svg component
import LoadingIcon from '@svg/LoadingIcon'; // svg component
import Loading from '@component/Common/loading';

// common function
import onSubmit from '@component/GDSTHSR/Common/onSubmit';
import {
    scrollToElement,
    triggerErrShake,
    addSeparator,
    getWindowCustomFunctions,
    THSRClearStorage,
    checkLigBoxOrDialogDisplay,
    getEarliestDate,
    getAllDatesInDetailData,
    formatThsrQtyReqBody
} from '@libs/core';
import { isMobile } from '@libs/globalVariables';
import { setGDSSessionData, getCheckThsrQty } from '@libs/apiCall';

// plugin
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

// redux actions
import {
    setTGRestore,
    setSessionPicked,
    setIsSubmitSuccess,
    setScrollTo,
    setIsTGDSSessionLoading,
    setTGDSSessionFetchFail,
    setSession,
    setTGReset,
} from '@redux/GDST/actions';
import { setTCurrentStep } from '@redux/THSR/actions';
import AddOnPriceTag from '@component/Common/AddOnPriceTag';

interface IGDetailProps extends ICommonDetailInfo {}

export const TDetailInfo: React.FC<IGDetailProps> = (props) => {
    const dispatch = useDispatch();
    const { formatPriceByCulture }: WindowCustomData = getWindowCustomFunctions();
    const { t } = useTranslation();
    const calendarBlockRef = useRef<HTMLDivElement>(null);
    const sessionBlockRef = useRef<HTMLDivElement>(null);
    const ticketBlockRef = useRef<HTMLDivElement>(null);
    const submitErrRef = useRef<HTMLDivElement>(null);

    const {
        calendar,
        GDetail,
        totalAmountN,
        salePriceSpread,
        showCrossellTag,
        TGNowListData,
        TGCalenderErr,
        TGSessionErr,
        TGTicketErr,
        TGDSSessionFetchFail,
        isTGDSSessionLoading,
        isSubmitSuccess,
        Scrollto,
        submitWatcher,
        TGSubmitErrMsg,
    } = useSelector((state: RootState) => state.GDST);
    const { GListData, ETID, Currency } = useSelector((state: RootState) => state.Common);
    const { THSRInfo } = useSelector((state: RootState) => state.THSR);
    const store_THSR = useSelector((state: RootState) => state.THSR);
    const { ThsrDiscountPrice, TicketsInfo }: ITHSRInfoData = THSRInfo;
    const hasSession = GDetail?.Sessions;
    const daysArrInMonth = useMemo(
        () => getAllDatesInDetailData(GDetail, 'calendarDate'),
        [GDetail]
    );

    const { dialogSwitch, setDialogSwitch, isCrossell } = props;

    const clickPCSubmitBtn = () => {
        if (isSubmitSuccess) return;
        //送出前清空高鐵相關sessionStorage
        THSRClearStorage();

        let reqBody = formatThsrQtyReqBody(
            store_THSR.THSRDetailData.Rounds,
            ETID,
            store_THSR?.THSRDepartTrain,
            store_THSR?.THSRReturnTrain,
            store_THSR?.THSRInfo
        );

        //需驗證高鐵是否還有庫存
        getCheckThsrQty({
            context: store_THSR,
            req: reqBody,
        }).then(async (res) => {
            if (res.every((child) => child?.HasQty)) {
                //有庫存
                const isSuccess = await onSubmit('checkData');
                if (typeof isSuccess === 'undefined') return;
                if (isSuccess.selectChecked) {
                    // 確認是否為迪士尼產品
                    if (checkLigBoxOrDialogDisplay(dialogSwitch, setDialogSwitch, 'disney')) {
                        setDialogSwitch((prev) => {
                            prev.disney = true;
                            return { ...prev };
                        });
                    } else {
                        dispatch(setIsSubmitSuccess(true));
                        onSubmit('redirect');
                    }
                } else if (
                    !isSuccess.selectChecked &&
                    'IsSuccess' in isSuccess.apiChecked &&
                    !isSuccess.apiChecked.IsSuccess
                ) {
                    // 驗價錯誤則顯示提示重整頁面光箱
                    setDialogSwitch((prev) => {
                        prev.verify = true;
                        return { ...prev };
                    });
                }
            } else {
                //無庫存or其他錯誤
                window.alert(t('THSR.detailInfo.noSeatErr', { symbol: '！' }));
                window.location.reload();
            }
        });
    };

    useEffect(() => {
        // 清除cache btn state(從上一頁導回來因cache造成btn維持disabled與loading的狀態)
        const resetBtn = (e: any) => {
            if (e.persisted) {
                console.log('This page was restored from the bfcache.');
                dispatch(setIsSubmitSuccess(false));
            }
        };
        window.addEventListener('pageshow', resetBtn);
        return () => window.removeEventListener('pageshow', resetBtn);
    }, [dispatch]);

    useEffect(() => {
        if (!Scrollto) return;
        dispatch(setScrollTo(null));
        if (!isMobile) return;
        if (Scrollto === scrollToRef.calender) {
            scrollToElement({ ref: calendarBlockRef });
        }
        if (hasSession && Scrollto === scrollToRef.session) {
            scrollToElement({ ref: sessionBlockRef });
        }
        if (Scrollto === scrollToRef.ticket) {
            scrollToElement({ ref: ticketBlockRef });
        }
        if (Scrollto === scrollToRef.submitErr) {
            scrollToElement({ ref: submitErrRef });
        }
    }, [Scrollto]);

    useEffect(() => {
        let calendarTimer: any;
        if (TGCalenderErr) {
            triggerErrShake({ ref: calendarBlockRef, timer: calendarTimer });
        }
        return () => {
            calendarTimer && clearTimeout(calendarTimer);
        };
    }, [TGCalenderErr, submitWatcher]);

    useEffect(() => {
        if (!hasSession) return;
        let sessionTimer: any;
        if (TGSessionErr) {
            triggerErrShake({ ref: sessionBlockRef, timer: sessionTimer });
        }
        return () => {
            sessionTimer && clearTimeout(sessionTimer);
        };
    }, [hasSession, TGSessionErr, submitWatcher]);

    useEffect(() => {
        let ticketTimer: any;
        if (TGTicketErr) {
            triggerErrShake({ ref: ticketBlockRef, timer: ticketTimer });
        }
        return () => {
            ticketTimer && clearTimeout(ticketTimer);
        };
    }, [TGTicketErr, submitWatcher]);

    return (
        GDetail && (
            <div className="detailInfo">
                <div className="selectedBlock">
                    <div className="resetBlock">
                        <div
                            onClick={() => {
                                dispatch(setTGReset());
                                if (TGNowListData.ExpectedUseDay && !GDetail.EarliestBuyDay) return;

                                hasSession &&
                                    daysArrInMonth.length !== 0 &&
                                    setGDSSessionData({
                                        dispatch,
                                        date: calendar ? calendar : getEarliestDate(daysArrInMonth), //detail的最早日避免list的日期還沒開放
                                        FormatID: TGNowListData.FormatID,
                                        ETID,
                                        context: {
                                            setGSessionLoading: setIsTGDSSessionLoading,
                                            setGSessionFetchFail: setTGDSSessionFetchFail,
                                            setSession,
                                            setSessionPicked,
                                        },
                                    });
                            }}
                        >
                            <ResetIcon color="#000000" />
                            <span>{t('THSR.detailInfo.allReset')}</span>
                        </div>
                    </div>
                    <div className="selectContainer">
                        <div className="calendarBlock" ref={calendarBlockRef}>
                            <div className="calendarTitle">
                                <div className="subTitle">{t('THSR.detailInfo.choose_date')}</div>
                                <div className={cx('errorMsg', { showError: !!TGCalenderErr })}>
                                    {TGCalenderErr}
                                </div>
                            </div>
                            <div className="calendarWrap">
                                {TGNowListData.ExpectedUseDay && !TGNowListData.EarliestBuyDay ? (
                                    <div className="noDates">
                                        {t('THSR.detailInfo.sorryForNoAvailableDates')}
                                    </div>
                                ) : (
                                    <CalendarBlock />
                                )}
                                {GDetail.Sessions && isTGDSSessionLoading && (
                                    <div className="loadingCalendar" />
                                )}
                            </div>
                            {/* <div className="calendarNotice">
                                    價格以 {GListData.SaleCurrency} 顯示
                                </div> */}
                        </div>

                        <div className="detailBlock">
                            {/* TODO: sessionBlock結構要改成自建THSR版*/}
                            {TGNowListData?.ExpectedUseDay &&
                                GDetail.Sessions &&
                                !(
                                    TGNowListData.ExpectedUseDay && !TGNowListData.EarliestBuyDay
                                ) && (
                                    <div className="sessionBlock" ref={sessionBlockRef}>
                                        <div className="sessionTitle">
                                            <div className="subTitle">
                                                {t('THSR.detailInfo.sessionAndTime')}
                                            </div>
                                            <div
                                                className={cx('errorMsg', {
                                                    showError: !!TGSessionErr,
                                                })}
                                            >
                                                {TGSessionErr}
                                            </div>
                                        </div>
                                        {isTGDSSessionLoading ? (
                                            <div className="loadBlock">
                                                <Loading
                                                    icon={<LoadingIcon />}
                                                    text={t('THSR.detailInfo.loading_session')}
                                                />
                                            </div>
                                        ) : TGDSSessionFetchFail ? (
                                            <div>{t('THSR.detailInfo.noSessionErr')}</div>
                                        ) : (
                                            <SessionBlock />
                                        )}
                                    </div>
                                )}
                            <div
                                className={cx('ticketBlock', {
                                    load_hide: isTGDSSessionLoading,
                                })}
                                ref={ticketBlockRef}
                            >
                                {/* TODO: TicketBlock結構要改成自建THSR版*/}
                                <div className="ticketTitleWrap">
                                    <div className="subTitle">
                                        {t('THSR.detailInfo.ticketAndNumber')}
                                    </div>
                                    <div className={cx('errorMsg', { showError: !!TGTicketErr })}>
                                        {TGTicketErr}
                                    </div>
                                    <div className="atLeast">
                                        <span>
                                            {t('THSR.detailInfo.needToBuy', { symbol: '≧' })}
                                        </span>
                                        <span>{TicketsInfo?.headCounts}</span>
                                    </div>
                                </div>
                                {TGNowListData.ExpectedUseDay && !TGNowListData.EarliestBuyDay ? (
                                    <div className="noTickets">
                                        {t('THSR.detailInfo.sorryForNoAvailableTickets')}
                                    </div>
                                ) : (
                                    <TicketBlock isCrossell={isCrossell} />
                                )}
                            </div>
                            <div
                                className={cx('priceArea', {
                                    load_hide: isTGDSSessionLoading,
                                })}
                            >
                                <div className="totalPrice">
                                    {isCrossell && showCrossellTag && (
                                        <div className="addOnPrice">
                                            <AddOnPriceTag />
                                        </div>
                                    )}
                                    <span className="totalPriceTxt">{t('THSR.detailInfo.totalPrice')}</span>
                                    <span className="saleCurrency">{GListData.SaleCurrency} </span>
                                    <span className="totalAmount">
                                        {addSeparator(
                                            formatPriceByCulture(
                                                Currency,
                                                totalAmountN + TicketsInfo?.totalPrice
                                            )
                                        )}
                                    </span>
                                </div>
                                <div className="originalPrice">
                                    <span>{GListData.SaleCurrency}</span>
                                    <span>
                                        {addSeparator(
                                            formatPriceByCulture(
                                                Currency,
                                                totalAmountN + TicketsInfo?.totalPrice + ThsrDiscountPrice + salePriceSpread
                                            )
                                        )}
                                    </span>
                                </div>
                                <div className="savePrice">
                                    <span>{t('THSR.detailInfo.saveMoney', { symbol: '- ' })}</span>
                                    <span>{`${addSeparator(ThsrDiscountPrice + salePriceSpread)}`}</span>
                                </div>
                            </div>
                            {TGSubmitErrMsg && (
                                <div className="submitErrMsg" ref={submitErrRef}>
                                    <IcRcln name="tooli" />
                                    <span>{TGSubmitErrMsg}</span>
                                </div>
                            )}
                            <div
                                className={cx('submitBlock', {
                                    load_hide: isTGDSSessionLoading,
                                })}
                            >
                                <div
                                    className="prevStep"
                                    onClick={() => {
                                        const listOpened = document.querySelector('.opened');
                                        // PC按回上一步後scroll to 目前打開的高鐵方案
                                        // 避免原本方案面板過高收合後看不到目前打開的面板
                                        listOpened.scrollIntoView({
                                            behavior: 'auto',
                                        });
                                        dispatch(setTCurrentStep(0));
                                        dispatch(setTGRestore());
                                    }}
                                >
                                    <span>
                                        {t('THSR.detailInfo.backToTHSRPanel', { symbol: '<' })}
                                    </span>
                                </div>
                                <div
                                    className={cx('submitBtn', {
                                        disabled:
                                            isSubmitSuccess ||
                                            (TGNowListData.ExpectedUseDay &&
                                                !TGNowListData.EarliestBuyDay),
                                    })}
                                    onClick={async () => {
                                        if (
                                            TGNowListData.ExpectedUseDay &&
                                            !TGNowListData.EarliestBuyDay
                                        )
                                        return;
                                        clickPCSubmitBtn();
                                    }}
                                >
                                    <div>{t('THSR.detailInfo.bookNow')}</div>
                                    {isSubmitSuccess && (
                                        <div className="reload">
                                            <Reload color="#ffffff" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    );
};

export default TDetailInfo;
